// import styles from './CompanyDetails.module.css';

import React, { useContext, useEffect } from 'react';
import { Alert, Flex, Form, Input, message, Radio, Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  handleErrorInputMessage,
  handleInputError,
} from '../../../core/utils/errorUtils';
import {
  FormCard,
  FormContainer,
  FormSectionTitle,
} from '@leedpulse-monorepo/components';
import { AccountType, ContextStatus } from '@leedpulse-monorepo/enums';
import CompanyCreationFormContext from '../../contexts/data/companies/CompanyCreationFormContext';
import { COMPANIES_PAGE_PATH } from '../../pages/companies/CompaniesPage';
import PhoneFormItem from './UserFormItems/PhoneFormItem';
import EmailFormItem from './UserFormItems/EmailFormItem';
import DebouncedUserSelect, { UserHit } from '../@shared/users/DebouncedUserSelect';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';

const MAX_COUNT_SELECT = 10;

const CompanyCreationForm: React.FC = () => {
  const navigate = useNavigate();
  const formContext = useContext(CompanyCreationFormContext);
  const authContext = useContext(AuthenticationContext);

  useEffect(() => {
    switch (formContext.status) {
      case ContextStatus.SUCCESS:
        message.success('Entreprise créée avec succès');
        navigate(COMPANIES_PAGE_PATH);
        break;
      case ContextStatus.ERROR:
        message.error('Erreur lors de la création de l\'entreprise');
        break;
      default:
        break;
    }
  }, [formContext.status, navigate]);

  return (
    <FormCard
      title="Nouvelle entreprise"
      showTitle={false}
      onCreated={() => formContext.submit()}
      isCreateLoading={formContext.status === ContextStatus.LOADING}
    >
      <Form layout="vertical">
        <FormSectionTitle title="Utilisateur principal" />
        <FormContainer>
          <Flex>
            <Form.Item label="Définir un superviseur">
              <Radio.Group value={formContext.user.createNewUser} onChange={(e) => formContext.onCreateNewUserChange(e.target.value as boolean)} block optionType="button" buttonStyle='solid'>
                <Radio value={true}>Nouveau</Radio>
                <Radio value={false}>Existant</Radio>
              </Radio.Group>
            </Form.Item>
          </Flex>

          {formContext.user.createNewUser ? (
            <>
              <Form.Item label="Prénom">
                <Input
                  value={formContext.user.firstname}
                  onChange={(e) => formContext.onFirstnameChange(e.target.value)}
                  placeholder="Jean"
                  autoFocus
                />
              </Form.Item>

              <Form.Item label="Nom">
                <Input
                  value={formContext.user.lastname}
                  onChange={(e) => formContext.onLastnameChange(e.target.value)}
                  placeholder="Dupont"
                  autoFocus
                />
              </Form.Item>

              <EmailFormItem />
              <PhoneFormItem />
            </>
          ) : (
            <Form.Item label="Utilisateur existant">
              <DebouncedUserSelect
                auth={authContext.authentication}
                placeholder="Rechercher par email…"
                value={formContext.user.userDoc?.uid}
                onHitSelect={(hit) => formContext.onUserDocChange(hit)}
                onChange={(val) => { if (!val) formContext.onUserDocChange(null); }}
                optionRender={(hit: UserHit) => `${hit.firstname} ${hit.lastname} - ${hit.email}`.trim()}
                showSearch
                customOptions={false}
                maxCount={MAX_COUNT_SELECT}
                allowClear
                />
            </Form.Item>
          )}
        </FormContainer>

        <FormSectionTitle title="Informations générales" />
        <FormContainer>
          <Form.Item
            required
            label="Nom"
            validateStatus={handleInputError(
              formContext.company.name,
              formContext.status
            )}
            help={handleErrorInputMessage(formContext.company.name, formContext.status)}
          >
            <Input
              value={formContext.company.name}
              onChange={(e) => formContext.onNameChange(e.target.value)}
              placeholder="Leedcorp"
            />
          </Form.Item>

          <Form.Item label="SIRET" layout="vertical">
            <Input
              value={formContext.company.siret}
              onChange={(e) => formContext.onSiretChange(e.target.value)}
              placeholder="379 264 435 17506"
            />
          </Form.Item>

          <Form.Item label="Notes" layout="vertical">
            <Input.TextArea
              value={formContext.company.notes}
              onChange={(e) => formContext.onNotesChange(e.target.value)}
              placeholder="Notes..."
              rows={3}
            />
          </Form.Item>
        </FormContainer>

        <FormSectionTitle title="Formule d'abonnement" />
        <FormContainer>
          <Flex>
            <Form.Item label="Choisir un type de compte">
              <Radio.Group value={formContext.payment.accountType} onChange={(e) => formContext.onAccountTypeChange(e.target.value as AccountType)} block optionType="button" buttonStyle='solid'>
                <Radio value={AccountType.COMPANY}>Gratuit</Radio>
                <Radio value={AccountType.SUBSCRIPTION} disabled>Payant</Radio>
              </Radio.Group>
            </Form.Item>
          </Flex>

          {formContext.payment.accountType === AccountType.SUBSCRIPTION ? (
            <Form.Item label="Formule d'abonnement" layout="vertical">
              <Select
                value={formContext.payment.plan}
                onChange={(value) => formContext.onPlanChange(value)}
                options={[
                  {
                    label: "Mensuel",
                    title: "monthly",
                    options: formContext.plans.monthly.map((plan) => ({ label: <Flex key={plan.id} justify="space-between"><span>{plan.name}</span><span>{plan.price?.formatted || '0€'}/mois</span></Flex>, value: plan.id }))
                  },
                  {
                    label: "Annuel",
                    title: "annual",
                    options: formContext.plans.annual.map((plan) => ({ label: <Flex key={plan.id} justify="space-between"><span>{plan.name}</span><span>{plan.price?.formatted || '0€'}/an</span></Flex>, value: plan.id }))
                  }
                ]}
              />
            </Form.Item>
          ) : (
            <Alert message="Le choix d'un plan se fera plus tard." type="info" />
          )}
        </FormContainer>

        {/* Stripe: mutual exclusivity - plan selected => ID only; no plan => Nouveau/Existant choice */}
        <FormSectionTitle
          title={
            formContext.payment.accountType === AccountType.SUBSCRIPTION
              ? 'Client Stripe (avec abonnement actif)'
              : 'Relier à un client Stripe'
          }
        />
        <FormContainer>
          {formContext.payment.accountType === AccountType.SUBSCRIPTION ? (
            <>
              <Alert
                message="Plan payant : Indiquez l'ID du client Stripe ayant effectué le paiement en amont."
                type="info"
                style={{ marginBottom: 16 }}
              />
              <Form.Item
                required
                label="ID Client Stripe"
                validateStatus={handleInputError(
                  formContext.stripe.id,
                  formContext.status
                )}
                help={handleErrorInputMessage(
                  formContext.stripe.id,
                  formContext.status
                )}
              >
                <Input
                  value={formContext.stripe.id}
                  onChange={(e) => formContext.onIdStripeChange(e.target.value)}
                  placeholder="cus_xxxxxxxxxxxxxxxx"
                />
              </Form.Item>
            </>
          ) : (
            <>
              <Flex>
                <Form.Item label="Relier à un client Stripe">
                  <Radio.Group
                    value={formContext.stripe.createNewCustomer}
                    onChange={(e) =>
                      formContext.onCreateNewCustomerChange(e.target.value as boolean)
                    }
                    block
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio value={true}>Nouveau</Radio>
                    <Radio value={false}>Existant</Radio>
                  </Radio.Group>
                </Form.Item>
              </Flex>
              {formContext.stripe.createNewCustomer ? (
                <Alert
                  message="Le client Stripe sera créé automatiquement."
                  type="info"
                />
              ) : (
                <Form.Item
                  required
                  label="ID Stripe"
                  validateStatus={handleInputError(
                    formContext.stripe.id,
                    formContext.status
                  )}
                  help={handleErrorInputMessage(
                    formContext.stripe.id,
                    formContext.status
                  )}
                >
                  <Input
                    value={formContext.stripe.id}
                    onChange={(e) =>
                      formContext.onIdStripeChange(e.target.value)
                    }
                    placeholder="cus_xxxxxxxxxxxxxxxx"
                  />
                </Form.Item>
              )}
            </>
          )}
        </FormContainer>

        {/*  Disabled for now */}
        {/* <FormContainer>
          <Form.Item label="Domaine d'activité" layout="vertical">
            <Select
              style={{ width: '100%' }}
              options={formContext.businessDomains}
              value={formContext.company.businessDomain}
              defaultValue={formContext.businessDomains[0].value}
              onChange={(value) => formContext.onBusinessDomainChange(value)}
            />
          </Form.Item>

          <Form.Item label="Description de l'activité (optionnel)" layout="vertical">
            <TextArea
              value={formContext.company.activityDescription}
              onChange={(e) => formContext.onActivityDescriptionChange(e.target.value)}
              placeholder="Décrivez l'activité, les services, la clientèle..."
              rows={4}
            />
          </Form.Item>
        </FormContainer> */}
      </Form>
    </FormCard>
  );
};

export default CompanyCreationForm;
